// Payment outcome (§10.1): status icon, title, body, key rows and the
// View receipt / Copy / Repeat / Done actions. Back goes Home.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import type { Payment, PaymentStatus } from '@/types/phase2.types';
import { formatIndianCurrency } from '@/utils/currency';
import { RAIL_LABELS } from '@/utils/payments';
import { formatLong } from '@/mock/clock';
import Button from '@/components/ui/Button/Button';
import PaymentReceipt from '../components/PaymentReceipt';
import { receiptCopyText, repeatPath } from '../components/payUtils';

const STATUS_UI: Record<PaymentStatus, { icon: typeof faCircleCheck; className: string; title: string }> = {
  SUCCESS: {
    icon: faCircleCheck,
    className: 'nova-outcome-ok',
    title: 'Payment successful',
  },
  PENDING: {
    icon: faClock,
    className: 'nova-outcome-warn',
    title: 'Payment in progress',
  },
  FAILED: {
    icon: faCircleXmark,
    className: 'nova-outcome-bad',
    title: 'Payment failed',
  },
  PENDING_APPROVAL: {
    icon: faClock,
    className: 'nova-outcome-warn',
    title: 'Sent for bank approval',
  },
  APPROVED: {
    icon: faCircleCheck,
    className: 'nova-outcome-ok',
    title: 'Payment approved',
  },
  REJECTED: {
    icon: faCircleXmark,
    className: 'nova-outcome-bad',
    title: 'Rejected',
  },
  CANCELLED: {
    icon: faCircleXmark,
    className: 'nova-outcome-bad',
    title: 'Cancelled',
  },
} as const;

const PaymentOutcome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const payment = (location.state as { payment?: Payment } | null)?.payment ?? null;
  const [receiptOpen, setReceiptOpen] = useState(false);

  useEffect(() => {
    if (!payment) navigate('/pay', { replace: true });
  }, [payment, navigate]);

  if (!payment) return null;

  const ui = STATUS_UI[payment.status] ?? {
    icon: faCircleXmark,
    className: 'nova-outcome-bad',
    title: 'Payment status unavailable',
  };
  const body =
    payment.status === 'SUCCESS'
      ? `Sent to ${payment.payeeName} via ${RAIL_LABELS[payment.rail]}.`
      : payment.status === 'PENDING'
        ? "Processing – we'll notify you"
        : payment.status === 'PENDING_APPROVAL'
          ? "The amount is on hold until the bank decides. We'll notify you."
          : payment.status === 'APPROVED'
            ? 'The checker approved this transaction.'
            : payment.status === 'REJECTED'
              ? `The checker rejected this transaction${payment.rejectionReason ?? payment.approval?.rejectionReason ? `: ${payment.rejectionReason ?? payment.approval?.rejectionReason}` : '.'} Money was not debited.`
              : payment.status === 'CANCELLED'
                ? 'Payment cancelled. The hold has been released.'
                : `${payment.failureReason ?? 'The bank could not process this payment.'} Money not debited.`;

  const theyReceive = payment.fx
    ? `${payment.fx.currency} ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(payment.fx.theyReceive)}`
    : null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(receiptCopyText(payment));
      toast.success('Receipt copied');
    } catch {
      toast.error('Could not copy the receipt.');
    }
  };

  const rows: Array<[string, string]> = [
    ['To', payment.payeeName],
    ['Destination', payment.destination],
    ['Mode', RAIL_LABELS[payment.rail]],
  ];
  if (payment.chargesRupees > 0) rows.push(['Charges', formatIndianCurrency(payment.chargesRupees)]);
  if (theyReceive) rows.push(['They receive', theyReceive]);
  rows.push(['Reference', payment.reference], ['Date', formatLong(payment.createdAt)]);
  if (payment.status === 'PENDING_APPROVAL') {
    rows.push(['Approval', payment.levelsRequired === 1 ? 'Needs 1 approval' : `${payment.levelsRequired ?? 2} approvals`]);
  }

  return (
    <div className="nova-outcome-page">
      <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/dashboard')}>
        <FontAwesomeIcon icon={faArrowLeft} />
      </button>

      <div className={`nova-outcome-icon ${ui.className}`}>
        <FontAwesomeIcon icon={ui.icon} size="3x" />
      </div>
      <h2 className="nova-outcome-title">{ui.title}</h2>
      <p className="nova-outcome-body">{body}</p>

      <div className="nova-outcome-amount">{formatIndianCurrency(payment.amountRupees)}</div>

      <dl className="nova-receipt-rows nova-outcome-rows">
        {rows.map(([label, value]) => (
          <div className="nova-receipt-row" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      <div className="nova-outcome-actions">
        {payment.status === 'PENDING_APPROVAL' && payment.approvalId && (
          <Button onClick={() => navigate(`/pay/approvals/${payment.approvalId}`)}>View request</Button>
        )}
        <button type="button" className="nova-link-btn" onClick={() => setReceiptOpen(true)}>
          View full receipt
        </button>
        <div className="nova-outcome-buttons">
          <Button variant="outline" onClick={() => void copy()}>
            <FontAwesomeIcon icon={faCopy} /> Copy
          </Button>
          <Button variant="outline" onClick={() => navigate(repeatPath(payment))}>
            Repeat
          </Button>
          <Button onClick={() => navigate('/dashboard')}>Done</Button>
        </div>
      </div>

      {receiptOpen && (
        <PaymentReceipt
          payment={payment}
          onClose={() => setReceiptOpen(false)}
          onRepeat={(p) => navigate(repeatPath(p))}
        />
      )}
    </div>
  );
};

export default PaymentOutcome;
